import { useState } from "react";
import { Layers, MapPin, Users, Check, X, Play } from "lucide-react";
import { Reveal, SectionHead, MaskedLines } from "@/components/Motion";
import CTA from "@/components/CTA";
import { MANIFESTO } from "@/lib/site";

const HOW = [
  {
    icon: Layers,
    title: "Google Maps SDK at the core",
    body: "VeloPaths is built on the Google Maps SDK — the same routing, tiles, and reliability you already trust — so navigation simply works, everywhere.",
  },
  {
    icon: MapPin,
    title: "Custom curated pins on top",
    body: "Instead of the default firehose of points of interest, we render our own pins: only VeloPaths-approved businesses, grouped by six rider-first categories.",
  },
  {
    icon: Users,
    title: "Humans do the vetting",
    body: "Every stop is reviewed by people who actually ride the route — often our local cycling partners — before it ever appears on the map.",
  },
];

const GENERIC = [
  "Every POI ever scraped, all at once",
  "Businesses buried under ad noise",
  "No concept of a bike-friendly detour",
  "Stale hours, dead listings, mystery pins",
];

const OURS = [
  "Approved local stops only — six categories",
  "Businesses featured intentionally, by subscription",
  "Detours designed for bikes, measured in minutes",
  "Curated and maintained by local cycling partners on the ground",
];

export default function About() {
  const [playing, setPlaying] = useState(false);
  return (
    <main data-testid="page-about">
      <section
        data-testid="about-hero"
        className="relative overflow-hidden bg-[#0B2B22] pb-20 pt-32 text-[#F4F7F4] sm:pb-28 sm:pt-40"
      >
        <div className="grain absolute inset-0" />
        <div className="absolute -right-24 -top-24 size-96 rounded-full bg-[#2D6A4F]/40 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-5xl px-6 sm:px-10">
          <p className="font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#7FB998]">
            Our story · the film
          </p>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            <MaskedLines lines={["Paved by Riders,", "Powered by Locals."]} />
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#F4F7F4]/70">
              VeloPaths exists because the best part of any ride is the stop you didn't plan — and
              generic maps were never going to find it for you. Press play for the full story.
            </p>
          </Reveal>
          <Reveal delay={0.45} className="mt-10">
            <div
              data-testid="about-video"
              className="relative overflow-hidden rounded-[2rem] border border-[#F4F7F4]/15 shadow-2xl"
            >
              {playing ? (
                <video
                  src="/videos/about-story-web.mp4"
                  poster="/videos/about-story-poster.jpg"
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  ref={(el) => {
                    if (el) el.play().catch(() => {});
                  }}
                  data-testid="about-video-player"
                  className="aspect-video w-full bg-black"
                />
              ) : (
                <button
                  onClick={() => setPlaying(true)}
                  data-testid="about-video-play-button"
                  aria-label="Play the VeloPaths story film"
                  className="group relative block w-full cursor-pointer"
                >
                  <img
                    src="/videos/about-story-poster.jpg"
                    alt="VeloPaths story film — presenter in a bike shop introducing the platform"
                    loading="lazy"
                    decoding="async"
                    className="aspect-video w-full object-cover"
                  />
                  <span className="absolute inset-0 bg-[#0B2B22]/30 transition-colors duration-300 group-hover:bg-[#0B2B22]/20" />
                  <span className="absolute inset-0 grid place-items-center">
                    <span className="grid size-20 place-items-center rounded-full bg-[#E05A47] text-white shadow-2xl transition-transform duration-300 group-hover:scale-110 sm:size-24">
                      <Play size={30} className="ml-1.5" fill="currentColor" />
                    </span>
                  </span>
                  <span className="absolute bottom-5 left-6 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white/85 sm:text-xs">
                    The VeloPaths Story · 7 min
                  </span>
                </button>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.55}>
            <p className="mt-6 max-w-3xl text-sm sm:text-base leading-relaxed text-[#F4F7F4]/60">
              Filmed inside a working bike shop, this seven-minute film tells the whole VeloPaths
              story: trail stories and community destinations, attractions worth a detour, how
              local businesses reach riders, and the 50/50 shared-value model that rewards the
              local partners who curate every route.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16">
        <Reveal>
          <p className="font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#2D6A4F]">
            The mission
          </p>
          <p className="mt-6 max-w-4xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-snug tracking-tight text-[#0B2B22]">
            Connect every cyclist with trusted local knowledge, great destinations, and the
            businesses that help cycling communities thrive.
          </p>
        </Reveal>
      </section>

      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <SectionHead
            overline="How VeloPaths works"
            title="Serious maps underneath. Human curation on top."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {HOW.map((h, i) => (
              <Reveal key={h.title} delay={i * 0.12} className="h-full">
                <div className="group h-full rounded-[1.75rem] border border-[#154234]/10 bg-[#F4F7F4] p-8 transition-transform duration-500 hover:-translate-y-1.5">
                  <span className="grid size-12 place-items-center rounded-2xl bg-[#0B2B22] text-[#7FB998] transition-colors duration-300 group-hover:bg-[#E05A47] group-hover:text-white">
                    <h.icon size={21} />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-[#0B2B22]">
                    {h.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#52635B]">{h.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16">
        <SectionHead
          overline="Why we're different"
          title="A map app finds places. VeloPaths finds your ride."
        />
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <Reveal className="h-full">
            <div className="h-full rounded-[1.75rem] border border-[#154234]/10 bg-white p-8">
              <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#52635B]">
                Generic map apps
              </p>
              <ul className="mt-6 space-y-4">
                {GENERIC.map((g) => (
                  <li key={g} className="flex items-start gap-3 text-sm text-[#52635B]">
                    <X size={16} className="mt-0.5 shrink-0 text-[#52635B]/50" />
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="h-full">
            <div className="h-full rounded-[1.75rem] bg-[#0B2B22] p-8 text-[#F4F7F4]">
              <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#7FB998]">
                VeloPaths
              </p>
              <ul className="mt-6 space-y-4">
                {OURS.map((o) => (
                  <li key={o} className="flex items-start gap-3 text-sm text-[#F4F7F4]/85">
                    <Check size={16} className="mt-0.5 shrink-0 text-[#E05A47]" />
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0B2B22] py-24 text-[#F4F7F4] sm:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <div className="grid items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <SectionHead
                dark
                overline="The long-term vision"
                title="From one trail town to hundreds."
                sub="Every partner that joins VeloPaths brings their town with them — its paths, its businesses, its character. The platform scales the way cycling communities actually form: one local network at a time, stitched into a map that spans regions."
              />
            </div>
            <Reveal delay={0.15} className="lg:col-span-4">
              <div className="rounded-2xl border border-[#F4F7F4]/15 p-7">
                <p className="font-mono text-3xl font-bold text-[#E05A47]">01 → ∞</p>
                <p className="mt-2 text-sm leading-relaxed text-[#F4F7F4]/65">
                  One platform. Unlimited towns, routes, and local cycling partners.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16">
        <SectionHead overline="The VeloPaths manifesto" title="What we ride by." />
        <div className="mt-14 grid gap-x-14 gap-y-12 md:grid-cols-2">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.1}>
              <div className="border-t border-[#154234]/20 pt-7">
                <p className="font-mono text-sm font-bold text-[#E05A47]">{m.n}</p>
                <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold tracking-tight text-[#0B2B22]">
                  {m.title}
                </h3>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#52635B]">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTA
        overline="Join the ride"
        title="Whether you ride, serve, or share local knowledge — there's a place for you."
        primary={{ label: "Get in touch", to: "/contact", testid: "btn-cta-contact" }}
        secondary={{ label: "Meet the platform", to: "/riders", testid: "btn-cta-meet-platform" }}
      />
    </main>
  );
}
